import React, {
  CSSProperties, useCallback, useMemo, useState,
} from 'react';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper';
import { Booth, DesignCanvasLayer } from 'models/cms/booths';
import StyledContainer, { StyledModal } from 'pages/booths/boothDesign/styled';
import InfoIcon from '@material-ui/icons/Info';
import GuideContent from '../../components/BoothVideoCallGuideContent';
import {
  NetworkingWidgetItem, useNetworkingWidget, useNetworkingWidgetLogic, WidgetAction,
} from '../useNetworkingWidget';

const StyledPaper = styled(Paper)`
  width: 100%;
  max-height: 100%;
  overflow: hidden;
  pointer-events: all;
`;

const WidgetContainer = styled.div`
  position: relative;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  overflow: hidden;
  color: black;

  & > * {
    flex: 0 0 auto;
  }
`;

const TitleContainer = styled.div`
  width: 100%;
`;

const TitleContent = styled.svg`
  font-family: Lato;
  font-style: normal;
  font-weight: bold;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const BtnGroupContainer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-around;
  flex-wrap: wrap;
  width: 100%;
`;

const BtnContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const BtnContainer = styled.div`
  position: relative;
  flex: 0 0 auto;
  cursor: pointer;
  color: ${({ color }) => color} !important;

  & svg {
    fill: ${({ color }) => color};
  }
`;

const BtnIconSpacing = styled.div<{ height: string }>`
  width: 100%;
  height: ${({ height }) => height};
`;

const BtnTextContainer = styled.div`
  position: relative;
  width: 100%;
  height: 40%;
  font-weight: bold;
`;

const BtnText = styled.svg<{ top: string }>`
  position: absolute;
  left: 0;
  top: ${({ top }) => top};
`;

const InfoBtn = styled.div<{ height: string; width: string; left: string }>`
  position: absolute;
  top: 0;
  left: ${({ left }) => left};
  cursor: pointer;
  height: ${({ height }) => height};
  width: ${({ width }) => width};

  & > svg {
    fill: #A2AAB3;
    height: 100%;
    width: 100%;
  }
`;

interface NetworkingWidgetItemButtonProps {
  item: NetworkingWidgetItem;
  booth: Booth;
  size: number;
  topSpacing: number;
  iconSize: number;
  middleSpacing: number;
  textSize: number;
  aspectRatio: number;
  handleItemClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const VideoCallInfo = ({
  left,
  height,
  width,
}: any) => {
  const [open, setOpen] = useState(false);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <InfoBtn
      left={left}
      height={height}
      width={width}
    >
      <InfoIcon onClick={handleOpen} />
      <StyledModal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <GuideContent
          handleClose={handleClose}
          isDesktop
        />
      </StyledModal>
    </InfoBtn>
  );
};

const NetworkingWidgetItemButton = ({
  item,
  booth,
  size,
  topSpacing = 10,
  iconSize = 40,
  middleSpacing = 10,
  textSize = 40,
  aspectRatio = 150,
  handleItemClick,
}: NetworkingWidgetItemButtonProps) => {
  const { IconComponent } = item;

  const buttonStyle: CSSProperties = useMemo(() => ({
    width: `${size}%`,
    paddingBottom: `${size / (aspectRatio / 100)}%`,
  }), [aspectRatio, size]);

  const iconWidth = iconSize * (aspectRatio / 100);
  const iconHeight = iconSize;

  const videoCallInfoIconSize = Math.min(iconWidth, iconHeight) * 0.6;

  return (
    <BtnContainer color={item.color} style={buttonStyle}>
      <BtnContent onClick={handleItemClick}>
        <BtnIconSpacing height={`${topSpacing}%`} />
        <IconComponent
          style={{ display: 'block', margin: 'auto' }}
          width={`${iconWidth}%`}
          height={`${iconHeight}%`}
          color={item.color}
        />
        <BtnIconSpacing height={`${middleSpacing}%`} />
        <BtnTextContainer>
          <BtnText
            viewBox={`0 0 ${(100 / textSize) * aspectRatio} 100`}
            preserveAspectRatio="xMinYMin meet"
            height="100%"
            width="100%"
            top={`${40 - textSize}%`}
          >
            <text x="50%" y="80%" fontSize="80" textAnchor="middle">{item.message}</text>
          </BtnText>
        </BtnTextContainer>
      </BtnContent>
      {
        item.actionType === WidgetAction.VideoCall && (
          <VideoCallInfo
            left={`${50 + (iconWidth * 0.6) / 2}%`}
            height={`${videoCallInfoIconSize * (aspectRatio / 100)}%`}
            width={`${videoCallInfoIconSize}%`}
          />
        )
      }
    </BtnContainer>
  );
};

interface NetworkingWidgetElementProps {
  layer: DesignCanvasLayer;
  booth: Booth;
  canvasAspectRatio: number;
}

const NetworkingWidgetElement: React.FC<NetworkingWidgetElementProps> = ({
  layer,
  booth,
  canvasAspectRatio,
}: NetworkingWidgetElementProps) => {
  const { greetingMessage, items } = useNetworkingWidget();
  const { checkIfItemDisabled, buildItemClickHandler } = useNetworkingWidgetLogic(booth);

  const calculatedItems = useMemo(() => (
    items
      .filter((item) => !checkIfItemDisabled(item))
      .map((item) => ({ ...item, handleItemClick: buildItemClickHandler(item) }))
  ), [items, checkIfItemDisabled, buildItemClickHandler]);

  const layerWidth = parseFloat(layer.width.replace('%', ''));
  const layerHeight = parseFloat(layer.height.replace('%', ''));

  // TODO: Configs - Title
  const titleAspectRatio = 1500;
  const titleMarginTopPercentage = 3;
  const titleMarginBottomPercentage = 1;
  // TODO: Configs - Button sizing
  const buttonSize = 40;
  const buttonAspectRatio = 175;
  // TODO: Configs - Button horizontal space divide percentage
  const buttonTopSpacing = 15;
  const buttonIconSize = 30;
  const buttonMiddleSpacing = 15;
  const buttonTextSize = 20;

  const titleContainerStyle = useMemo(() => ({
    marginTop: `${titleMarginTopPercentage}%`,
    marginBottom: `${titleMarginBottomPercentage}%`,
    paddingBottom: `${100 / (titleAspectRatio / 100)}%`,
  }), [titleAspectRatio, titleMarginBottomPercentage, titleMarginTopPercentage]);
  const titleContentStyle = useMemo(() => ({
    marginTop: `${titleMarginTopPercentage}%`,
  }), [titleMarginTopPercentage]);

  const widgetAspectRatio = (layerWidth / layerHeight) * canvasAspectRatio;

  if (calculatedItems.length === 0) return null;

  return (
    <StyledContainer
      width={layer.width}
      height={layer.height}
      positionX={layer.positionX}
      positionY={layer.positionY}
      disabled
    >
      <StyledPaper elevation={3}>
        <WidgetContainer>
          <TitleContainer style={titleContainerStyle}>
            <TitleContent
              style={titleContentStyle}
              width="100%"
              viewBox={`0 0 ${titleAspectRatio} 100`}
              preserveAspectRatio="xMinYMin meet"
            >
              <text x="50%" y="80%" fontSize="80" textAnchor="middle">{greetingMessage}</text>
            </TitleContent>
          </TitleContainer>
          <BtnGroupContainer>
            {calculatedItems.map((item) => (
              <NetworkingWidgetItemButton
                key={item.id}
                item={item}
                booth={booth}
                size={buttonSize}
                iconSize={buttonIconSize}
                textSize={buttonTextSize}
                aspectRatio={buttonAspectRatio}
                topSpacing={buttonTopSpacing}
                middleSpacing={buttonMiddleSpacing}
                handleItemClick={item.handleItemClick}
              />
            ))}
          </BtnGroupContainer>
        </WidgetContainer>
      </StyledPaper>
    </StyledContainer>
  );
};

export default NetworkingWidgetElement;
